import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE, DISABLED_FEATURES } from '../config/api';
import { getThumbnailUrl } from '../lib/utils';
import { 
  Award, 
  BookOpen, 
  Users, 
  TrendingUp, 
  ArrowRight, 
  CheckCircle, 
  Target, 
  Brain, 
  Sparkles, 
  Bot, 
  Shield, 
  Zap, 
  LineChart, 
  GraduationCap, 
  Building2, 
  Briefcase, 
  ChevronRight, 
  FlaskConical, 
  BarChart3, 
  Globe, 
  FileCheck, 
  Gauge, 
  Route, 
  BadgeCheck, 
  Mail, 
  MapPin, 
  Linkedin, 
  Star, 
  Play, 
  Bell, 
  Lock, 
  Cpu, 
  Network, 
  ClipboardCheck, 
  Trophy, 
  BookMarked, 
  Layers, 
  Settings, 
  AlertTriangle, 
  Calendar, 
  CreditCard, 
  Wallet, 
  Check 
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
const API = `${API_BASE}/api`;

// Cerebro Logo URL - Teal/Gold Neural Brain Hexagon
const CEREBRO_LOGO = "https://customer-assets.emergentagent.com/job_cpd-compass/artifacts/km6w43n1_Teal%20Cerebro%20Transp.png";

// Feature Card Component
const FeatureCard = ({ icon: Icon, title, description, highlight = false, primaryColor, secondaryColor, accentColor, headingColor, textColor }) => {
  const cardAccent = accentColor || primaryColor || '#14b8a6';
  const cardSecondary = secondaryColor || '#f59e0b';
  return (
    <div 
      className="group relative p-6 rounded-2xl border transition-all duration-300 hover:scale-[1.02] bg-slate-800/50"
      style={{
        borderColor: highlight ? `${cardSecondary}40` : 'rgba(51, 65, 85, 0.5)',
        background: highlight ? `linear-gradient(135deg, ${cardSecondary}15, ${cardAccent}15)` : undefined
      }}
    >
      <div 
        className="inline-flex p-3 rounded-xl mb-4"
        style={{ 
          backgroundColor: `${cardAccent}20`,
          color: cardAccent
        }}
      >
        <Icon className="h-6 w-6" style={{ color: cardAccent }} />
      </div>
      <h3 className="text-lg font-semibold mb-2" style={{ color: headingColor || '#ffffff' }}>{title}</h3>
      <p className="text-sm leading-relaxed" style={{ color: textColor || '#94a3b8' }}>{description}</p>
    </div>
  );
};

// Pricing Card Component
const PricingCard = ({ tier, price, period, features, highlighted = false, cta = "Get Started", primaryColor }) => (
  <div className={`relative rounded-2xl p-8 transition-all duration-300 ${
    highlighted 
      ? 'bg-gradient-to-b from-amber-500/20 to-slate-800/80 border-2 border-amber-500/50 scale-105 shadow-xl shadow-amber-500/10' 
      : 'bg-slate-800/50 border border-slate-700/50 hover:border-slate-600'
  }`}>
    {highlighted && (
      <div className="absolute -top-4 left-1/2 -translate-x-1/2">
        <span className="px-4 py-1.5 bg-amber-500 text-slate-900 text-sm font-bold rounded-full">
          MOST POPULAR
        </span>
      </div>
    )}
    <div className="text-center mb-6">
      <h3 className="text-xl font-bold text-white mb-2">{tier}</h3>
      <div className="flex items-baseline justify-center gap-1">
        <span className="text-4xl font-bold text-white">{price}</span>
        {period && <span className="text-slate-400">/{period}</span>}
      </div>
    </div>
    <ul className="space-y-3 mb-8">
      {features.map((feature, i) => (
        <li key={i} className="flex items-start gap-3">
          <Check className={`h-5 w-5 mt-0.5 flex-shrink-0 ${highlighted ? 'text-amber-400' : 'text-teal-400'}`} />
          <span className="text-sm text-slate-300">{feature}</span>
        </li>
      ))}
    </ul>
    <Link
      to="/register"
      className={`block w-full py-3 px-6 rounded-xl font-semibold text-center transition-all ${
        highlighted 
          ? 'bg-amber-500 hover:bg-amber-600 text-slate-900' 
          : 'bg-slate-700 hover:bg-slate-600 text-white'
      }`}
    >
      {cta}
    </Link>
  </div>
);

export default function LandingPage() {
  const { isAuthenticated } = useAuth();
  const { tenant, loading } = useTenant();
  const navigate = useNavigate();

  // Dynamic branding parameters
  const isMaster = !tenant || tenant.isMaster;
  const orgName = isMaster ? 'Cerebro Core' : (tenant?.name || 'Cerebro Core');
  const branding = tenant?.branding || {};
  const primaryColor = (!isMaster && branding.primaryColor) ? branding.primaryColor : '#14b8a6';
  const secondaryColor = (!isMaster && branding.secondaryColor) ? branding.secondaryColor : '#f59e0b';
  const accentColor = (!isMaster && branding.accentColor) ? branding.accentColor : '#0d9488';
  const headingColor = (!isMaster && branding.headingColor) ? branding.headingColor : '#ffffff';
  const textColor = (!isMaster && branding.textColor) ? branding.textColor : '#94a3b8';
  const logoUrl = (!isMaster && branding.logoUrl) ? branding.logoUrl : CEREBRO_LOGO;
  const loginBackgroundUrl = !isMaster ? branding.loginBackgroundUrl : null;
  const isSSOEnabled = !isMaster && tenant?.sso?.enabled;
  const landingPage = !isMaster ? (branding.landingPage || {}) : {};

  const [courses, setCourses] = useState([]);
  const [learningLabs, setLearningLabs] = useState([]);
  const [stats, setStats] = useState({
    organizations: 15,
    professionals: 2500,
    courses: 50,
    completionRate: 94
  });

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const orgId = tenant?.orgId;
        const [statsRes, coursesRes, labsRes] = await Promise.all([
          axios.get(`${API}/public/stats`).catch(() => null),
          axios.get(`${API}/public/courses`, { params: { org_id: orgId || undefined, limit: 6 } }).catch(() => 
            axios.get(`${API}/courses`, { params: { limit: 6 } }).catch(() => ({ data: [] }))
          ),
          axios.get(`${API}/labs`, { params: { limit: 4 } }).catch(() => ({ data: { labs: [] } }))
        ]);

        if (statsRes?.data) setStats(statsRes.data);

        let rawCourses = coursesRes?.data?.courses || coursesRes?.data || [];
        if ((!Array.isArray(rawCourses) || rawCourses.length === 0) && orgId) {
          try {
            const fallback = await axios.get(`${API}/public/courses`, { params: { limit: 6 } });
            rawCourses = fallback.data?.courses || fallback.data || [];
          } catch {}
        }
        setCourses(Array.isArray(rawCourses) ? rawCourses : []);

        const rawLabs = labsRes?.data?.labs || labsRes?.data?.learning_labs || labsRes?.data || [];
        setLearningLabs(Array.isArray(rawLabs) ? rawLabs : []);
      } catch (err) {
        console.error('Stats / content not available:', err);
      }
    };
    fetchContent();
  }, [tenant?.orgId]);

  // Show loading state while resolving tenant
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="h-8 w-8 border-2 border-teal-500/30 border-t-teal-500 rounded-full animate-spin" />
      </div>
    );
  }

  // Enterprise Features
  const enterpriseFeatures = [
    {
      icon: Brain,
      title: "AI Course Generation",
      description: "Create comprehensive training courses in minutes with our AI engine. Every course is curated and approved by human experts ensuring Human-In-The-Loop (HITL) quality protocols.",
      highlight: true
    },
    {
      icon: Globe,
      title: "LMS-as-a-Service",
      description: "Deploy your own branded learning management system instantly. White-label solution with your domain, branding, and complete control - no infrastructure required.",
      highlight: true
    },
    {
      icon: FlaskConical,
      title: "Learning Labs",
      description: "Interactive sandbox environments where learners practice real-world scenarios in safe, simulated settings."
    },
    {
      icon: Network,
      title: "Multi-Tenant Architecture",
      description: "Deploy separate branded instances for different business units, franchises, or client organisations."
    },
    {
      icon: ClipboardCheck,
      title: "NAVI Coach & Assessment Builder",
      description: "AI-powered coaching with custom quizzes, MCQs, case studies, and practical assessments. Auto-grading and detailed analytics included."
    },
    {
      icon: Route,
      title: "Learning Pathways",
      description: "Design structured career progression paths with prerequisite courses and competency milestones."
    },
    {
      icon: Trophy,
      title: "Gamification Engine",
      description: "Boost engagement with points, badges, leaderboards, and achievement systems that motivate learners."
    },
    {
      icon: Users,
      title: "Learning Strategist Access",
      description: "Dedicated Organisational Learning Strategist for WSP and ATR assistance, mapping learning pathways and courses aligned to your skills plan.",
      highlight: true
    },
    {
      icon: BarChart3,
      title: "WSP & ATR Reporting",
      description: "Automated Workplace Skills Plan submissions and Annual Training Reports for SETA compliance."
    },
    {
      icon: Shield,
      title: "SCORM & AICC Compliance",
      description: "Import and track third-party content with full SCORM 1.2/2004 and AICC standard support."
    },
    {
      icon: Settings,
      title: "Role Readiness Assessments",
      description: "Evaluate employee competencies against role requirements with gap analysis and development plans."
    },
    {
      icon: Award,
      title: "Certificate Generation",
      description: "Automatically generate branded PDF certificates with QR verification codes upon course completion."
    },
    {
      icon: Lock,
      title: "SSO Integration",
      description: "Seamless Azure AD, Google, and SAML single sign-on for enterprise identity management."
    },
    {
      icon: LineChart,
      title: "Advanced Analytics",
      description: "Real-time dashboards tracking learner progress, completion rates, and organisational skills development."
    },
    {
      icon: Award,
      title: "Candidacy Portal",
      description: "Professional designation readiness management - integrating knowledge acquisition, practical application, workplace experience, competency development, mentoring, portfolio management, and professional designation readiness.",
      highlight: true
    }
  ];

  // Individual Features (filtered by DISABLED_FEATURES)
  const allIndividualFeatures = [
    {
      icon: GraduationCap,
      title: "Navigate Academy",
      description: "Access ICA and ICITP professional qualifications with globally recognised certifications in compliance and IT governance.",
      highlight: true,
      featureKey: 'navigate_academy'
    },
    {
      icon: Bell,
      title: "Regulatory Alerts",
      description: "Real-time notifications from FSCA, FIC, SARB, and international regulators. Stay ahead of compliance changes.",
      highlight: true,
      featureKey: 'regulatory_alerts'
    },
    {
      icon: Award,
      title: "Candidacy",
      description: "Professional designation readiness management - integrating knowledge acquisition, practical application, workplace experience, competency development, mentoring, portfolio management, and professional designation readiness in one intelligent ecosystem.",
      highlight: true,
      featureKey: 'candidacy'
    },
    {
      icon: BookMarked,
      title: "Self-Paced Learning",
      description: "Learn at your own pace with lifetime access to course materials, videos, and downloadable resources."
    },
    {
      icon: Target,
      title: "CPD Tracking",
      description: "Automatically log and track your Continuing Professional Development points across all completed courses."
    },
    {
      icon: Award,
      title: "Digital Certificates",
      description: "Earn verifiable digital certificates and badges to showcase your achievements on LinkedIn."
    },
    {
      icon: Brain,
      title: "AI Learning Copilot",
      description: "Get personalised learning recommendations and instant answers to your compliance questions."
    },
    {
      icon: Layers,
      title: "Structured Pathways",
      description: "Follow curated learning paths from foundational to advanced levels in your chosen specialisation."
    },
    {
      icon: Globe,
      title: "Global Standards",
      description: "Content aligned to international frameworks including FATF, Basel, GDPR, and ISO standards."
    }
  ];

  // Filter out disabled features
  const individualFeatures = allIndividualFeatures.filter(
    f => !f.featureKey || !DISABLED_FEATURES[f.featureKey]
  );

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/90 backdrop-blur-xl border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src={logoUrl} 
                alt={orgName} 
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = CEREBRO_LOGO;
                }}
                className="h-10 w-auto object-contain" 
              />
              <span className="text-white font-bold text-lg hidden sm:block">{orgName}</span>
            </Link>

            {/* Quick nav links */}
            <div className="hidden md:flex items-center gap-6 text-sm font-medium text-slate-300">
              <Link to="/courses" className="hover:text-white transition-colors">Courses</Link>
              <Link to="/labs" className="hover:text-white transition-colors">Learning Labs</Link>
              <Link to="/learning-pathways" className="hover:text-white transition-colors">Pathways</Link>
              <a href="#features" className="hover:text-white transition-colors">Features</a>
            </div>

            <div className="flex items-center gap-3">
              {isAuthenticated ? (
                <Link
                  to="/dashboard"
                  className="text-slate-900 text-sm font-semibold px-4 py-2 rounded-lg transition-all shadow"
                  style={{ backgroundColor: primaryColor }}
                >
                  My Dashboard
                </Link>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="text-slate-900 text-sm font-semibold px-4 py-2 rounded-lg transition-all shadow hover:opacity-90"
                    style={{ backgroundColor: primaryColor }}
                  >
                    Sign In
                  </Link>
                  {isSSOEnabled && (
                    <Link
                      to="/enterprise-sso"
                      className="border border-slate-700 hover:bg-slate-800 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors flex items-center gap-1.5"
                    >
                      <Shield className="h-3.5 w-3.5" />
                      <span>SSO</span>
                    </Link>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        {/* Optional Announcement Banner */}
        {landingPage.announcement_enabled && landingPage.announcement_text && (
          <div 
            className="mb-8 max-w-4xl mx-auto px-4 py-2.5 rounded-xl border text-center text-sm font-semibold flex items-center justify-center gap-2 shadow-lg backdrop-blur-md"
            style={{ 
              backgroundColor: `${primaryColor}20`, 
              borderColor: `${primaryColor}60`,
              color: '#ffffff'
            }}
          >
            <Sparkles className="h-4 w-4 shrink-0" style={{ color: primaryColor }} />
            <span>{landingPage.announcement_text}</span>
          </div>
        )}
        {/* Background Effects */}
        <div className="absolute inset-0">
          {loginBackgroundUrl ? (
            <div 
              className="absolute inset-0 bg-cover bg-center opacity-20"
              style={{ backgroundImage: `url(${loginBackgroundUrl})` }}
            />
          ) : null}
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900/90 to-slate-950" />
          <div 
            className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] rounded-full blur-3xl opacity-20" 
            style={{ backgroundColor: primaryColor }}
          />
          <div className="absolute bottom-0 right-0 w-[600px] h-[400px] bg-amber-500/5 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <div className="relative">
              <div 
                className="absolute inset-0 blur-[100px] rounded-full scale-150 opacity-25" 
                style={{ backgroundColor: primaryColor }}
              />
              <img 
                src={logoUrl} 
                alt={orgName} 
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = CEREBRO_LOGO;
                }}
                className="relative h-40 sm:h-52 max-w-[340px] w-auto object-contain"
              />
            </div>
          </div>

          {/* Title */}
          <div className="text-center mb-8">
            <div 
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-6"
              style={{
                backgroundColor: `${accentColor}1A`,
                borderColor: `${accentColor}40`
              }}
            >
              <Sparkles className="h-4 w-4" style={{ color: accentColor }} />
              <span className="text-sm" style={{ color: isMaster ? '#cbd5e1' : textColor }}>
                {landingPage.badge_text || (isMaster ? 'A Workforce Capability Platform' : `Official ${orgName} Learning Platform`)}
              </span>
            </div>

            {isMaster ? (
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                We Help Organisations Make{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-amber-400">
                  Compliance Training Relevant
                </span>
              </h1>
            ) : (
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight" style={{ color: headingColor }}>
                {landingPage.hero_title ? (
                  landingPage.hero_title
                ) : (
                  <>
                    Welcome to{' '}
                    <span 
                      className="text-transparent bg-clip-text"
                      style={{ backgroundImage: `linear-gradient(to right, ${primaryColor}, ${secondaryColor})` }}
                    >
                      {orgName} Learning Academy
                    </span>
                  </>
                )}
              </h1>
            )}

            {isMaster ? (
              <>
                <p className="text-xl text-slate-400 max-w-4xl mx-auto leading-relaxed mb-6">
                  Compliance training is difficult to keep current, engaging and relevant as regulations, risks and business requirements continuously change.
                </p>
                <p className="text-base text-slate-400 max-w-3xl mx-auto leading-relaxed mb-4">
                  Cerebro Core transforms static training into continuous, intelligent learning — helping organisations keep content current, personalise learning by role and competency, improve engagement, identify skills gaps and measure real understanding.
                </p>
                <p className="text-lg font-semibold max-w-3xl mx-auto" style={{ color: primaryColor }}>
                  Move beyond course completion. Build measurable workforce capability.
                </p>
              </>
            ) : (
              landingPage.hero_subtitle ? (
                <p className="text-xl max-w-4xl mx-auto leading-relaxed mb-6 whitespace-pre-line" style={{ color: textColor }}>
                  {landingPage.hero_subtitle}
                </p>
              ) : (
                <>
                  <p className="text-xl max-w-4xl mx-auto leading-relaxed mb-6" style={{ color: textColor }}>
                    Accelerate your professional capabilities with enterprise compliance courses, interactive simulations, and structured career pathways designed for your team.
                  </p>
                  <p className="text-base max-w-3xl mx-auto leading-relaxed mb-4" style={{ color: textColor }}>
                    Continuous, intelligent learning and capability development curated specifically for {orgName}.
                  </p>
                  <p className="text-lg font-semibold max-w-3xl mx-auto" style={{ color: primaryColor }}>
                    Move beyond course completion. Build measurable workforce capability.
                  </p>
                </>
              )
            )}
          </div>

          {/* Enterprise Only Badge */}
          <div className="flex justify-center mb-12">
            <div className="inline-flex p-1.5 bg-slate-800/50 rounded-xl border border-slate-700">
              <div
                className="flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-sm text-slate-900 shadow-lg"
                style={{ backgroundColor: primaryColor }}
                data-testid="tab-enterprise"
              >
                <Building2 className="h-4 w-4" />
                {isMaster ? 'For Enterprises' : `${orgName} Enterprise`}
              </div>
            </div>
          </div>

          {/* Value Proposition */}
          <div className="text-center mb-12">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4" style={{ color: headingColor }}>
                {isMaster ? 'Scale Learning Across Your Organisation' : `Empower ${orgName} With Scalable Learning`}
              </h2>
              <p className="mb-8" style={{ color: textColor }}>
                Deploy on-demand training, track compliance, and develop your workforce with 
                enterprise-grade tools designed for regulated industries.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to={landingPage.cta_link || "/courses"}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 text-slate-900 font-semibold rounded-xl transition-all shadow-lg hover:opacity-95"
                  style={{ backgroundColor: primaryColor, boxShadow: `0 10px 25px -5px ${primaryColor}40` }}
                >
                  <BookOpen className="h-5 w-5" />
                  {landingPage.cta_text || "Explore Courses"}
                </Link>
                {isSSOEnabled ? (
                  <Link
                    to="/enterprise-sso"
                    className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-slate-800 text-white font-semibold rounded-xl border border-slate-700 hover:bg-slate-700 transition-all"
                  >
                    <Shield className="h-5 w-5" />
                    Sign In with SSO
                  </Link>
                ) : (
                  <Link
                    to="/login"
                    className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-slate-800 text-white font-semibold rounded-xl border border-slate-700 hover:bg-slate-700 transition-all"
                  >
                    Sign In
                  </Link>
                )}
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          {landingPage.show_stats !== false && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              {(!isMaster ? [
                { value: `${courses.length || stats.courses}+`, label: 'Courses' },
                { value: `${learningLabs.length || 8}+`, label: 'Learning Labs' },
                { value: '4+', label: 'Pathways' },
                { value: '1,000+', label: 'Active Learners' }
              ] : [
                { value: `${stats.organizations}+`, label: 'Organisations' },
                { value: `${stats.professionals}+`, label: 'Professionals' },
                { value: `${stats.courses}+`, label: 'Courses' },
                { value: `${stats.completionRate}%`, label: 'Completion Rate' }
              ]).map((stat, idx) => (
                <div key={idx} className="text-center p-4 rounded-xl bg-slate-800/30 border border-slate-800">
                  <p className="text-3xl font-bold" style={{ color: primaryColor }}>{stat.value}</p>
                  <p className="text-sm text-slate-500 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Featured Courses Section (when courses available and enabled) */}
      {landingPage.show_courses !== false && courses.length > 0 && (
        <section className="py-20 bg-slate-900/50 border-t border-slate-800/80">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-12">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-800/60 border border-slate-700 mb-4">
                  <BookOpen className="h-4 w-4" style={{ color: primaryColor }} />
                  <span className="text-xs text-slate-300 uppercase tracking-wider font-semibold">Featured Courses</span>
                </div>
                <h2 className="text-3xl font-bold tracking-tight" style={{ color: headingColor }}>
                  Curated Courses for <span style={{ color: primaryColor }}>{orgName}</span>
                </h2>
                <p className="text-sm mt-1" style={{ color: textColor }}>
                  High-impact compliance, regulatory and skills courses designed for your team.
                </p>
              </div>
              <Link 
                to="/courses"
                className="inline-flex items-center gap-2 text-sm font-semibold hover:opacity-80 transition-opacity"
                style={{ color: primaryColor }}
              >
                <span>View Full Catalog</span>
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {courses.slice(0, 6).map(course => (
                <div
                  key={course.course_id || course._id}
                  onClick={() => navigate(`/courses/${course.course_id}`)}
                  className="group cursor-pointer bg-slate-900/70 rounded-2xl overflow-hidden border border-slate-800 hover:border-slate-700 hover:scale-[1.02] transition-all duration-300 flex flex-col h-full shadow-lg"
                >
                  <div className="aspect-video relative overflow-hidden bg-slate-800">
                    <img 
                      src={getThumbnailUrl(course.thumbnail_url)}
                      alt={course.title}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&auto=format&fit=crop&q=60';
                      }}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    {course.is_featured && (
                      <span 
                        className="absolute top-3 left-3 px-2.5 py-1 text-white text-xs font-semibold rounded-md shadow"
                        style={{ backgroundColor: primaryColor }}
                      >
                        Featured
                      </span>
                    )}
                    {course.category && (
                      <span className="absolute bottom-3 left-3 px-2 py-0.5 bg-black/70 backdrop-blur-sm text-slate-200 text-xs rounded">
                        {course.category}
                      </span>
                    )}
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <h3 className="font-semibold text-white mb-2 line-clamp-2 group-hover:text-amber-400 transition-colors">
                      {course.title}
                    </h3>
                    <p className="text-sm text-slate-400 line-clamp-2 mb-4 flex-1">
                      {course.description || 'Enhance your professional skills with this comprehensive course module.'}
                    </p>
                    <div className="flex items-center justify-between pt-3 border-t border-slate-800 text-xs text-slate-400">
                      <span>{course.duration || '2-3 hours'}{course.cpd_points ? ` • ${course.cpd_points} CPD` : ''}</span>
                      <span className="font-semibold inline-flex items-center gap-1" style={{ color: primaryColor }}>
                        Start <ChevronRight className="h-3.5 w-3.5" />
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Learning Labs Section (when labs available and enabled) */}
      {landingPage.show_labs !== false && learningLabs.length > 0 && (
        <section className="py-20 bg-slate-950 border-t border-slate-800/80">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-12">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-800/60 border border-slate-700 mb-4">
                  <FlaskConical className="h-4 w-4" style={{ color: primaryColor }} />
                  <span className="text-xs text-slate-300 uppercase tracking-wider font-semibold">Interactive Simulations</span>
                </div>
                <h2 className="text-3xl font-bold tracking-tight" style={{ color: headingColor }}>
                  Learning Labs
                </h2>
                <p className="text-sm mt-1" style={{ color: textColor }}>
                  Hands-on scenario simulations and practical decision-making assessments.
                </p>
              </div>
              <Link 
                to="/labs" 
                className="inline-flex items-center gap-2 text-sm font-semibold hover:opacity-80 transition-opacity"
                style={{ color: primaryColor }}
              >
                <span>View All Labs</span>
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {learningLabs.slice(0, 4).map(lab => (
                <div 
                  key={lab.lab_id}
                  onClick={() => navigate(`/labs/${lab.lab_id}`)}
                  className="group cursor-pointer bg-slate-900/60 rounded-2xl p-6 border border-slate-800 hover:border-slate-700 transition-all flex flex-col justify-between"
                >
                  <div>
                    <div 
                      className="h-12 w-12 rounded-xl flex items-center justify-center mb-4"
                      style={{ backgroundColor: `${primaryColor}20`, color: primaryColor }}
                    >
                      <FlaskConical className="h-6 w-6" />
                    </div>
                    <h3 className="font-semibold text-white mb-2 group-hover:opacity-90">
                      {lab.title}
                    </h3>
                    <p className="text-xs text-slate-400 line-clamp-2 mb-4">
                      {lab.description || 'Interactive simulation exercise'}
                    </p>
                  </div>
                  <div 
                    className="flex items-center justify-between text-xs font-semibold pt-3 border-t border-slate-800"
                    style={{ color: primaryColor }}
                  >
                    <span>Launch Lab</span>
                    <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Features Section */}
      {landingPage.show_features !== false && (
      <section id="features" className="py-24 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header with Tabs */}
          <div className="text-center mb-16">
            <div 
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
              style={{
                backgroundColor: `${accentColor}1A`,
                borderColor: `${accentColor}40`,
                borderWidth: '1px'
              }}
            >
              <Zap className="h-4 w-4" style={{ color: accentColor }} />
              <span className="text-sm font-medium" style={{ color: accentColor }}>Powerful Features</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: headingColor }}>
              Everything You Need to{' '}
              <span style={{ color: accentColor }}>Transform Learning</span>
            </h2>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: textColor }}>
              Comprehensive tools for enterprise learning management, compliance tracking, and workforce development.
            </p>
          </div>

          {/* Features Grid - Enterprise Only */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {enterpriseFeatures.map((feature, idx) => (
              <FeatureCard 
                key={idx} 
                {...feature} 
                primaryColor={primaryColor}
                secondaryColor={secondaryColor}
                accentColor={accentColor}
                headingColor={headingColor}
                textColor={textColor}
              />
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Platform Showcase */}
      <section className="py-24 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div 
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
                style={{
                  backgroundColor: `${accentColor}1A`,
                  borderColor: `${accentColor}40`,
                  borderWidth: '1px'
                }}
              >
                <Brain className="h-4 w-4" style={{ color: accentColor }} />
                <span className="text-sm font-medium" style={{ color: accentColor }}>AI-Powered Platform</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ color: headingColor }}>
                Intelligence Built Into{' '}
                <span style={{ color: accentColor }}>Every Layer</span>
              </h2>
              <p className="text-lg mb-8 leading-relaxed" style={{ color: textColor }}>
                From AI-generated courses to intelligent recommendations, Cerebro leverages 
                cutting-edge technology to deliver personalised, effective learning experiences.
              </p>
              
              <div className="space-y-4">
                {[
                  { icon: Bot, title: "AI Course Generation", desc: "Turn documents, URLs or simple prompts into engaging courses in minutes, with human-in-the-loop review ensuring content is accurate, relevant and ready to publish" },
                  { icon: Sparkles, title: "Smart Recommendations", desc: "Personalised learning paths based on goals" },
                  { icon: Bell, title: "Real-time Alerts", desc: "Stay updated with regulatory changes" },
                  { icon: BarChart3, title: "Predictive Analytics", desc: "Identify skill gaps before they impact performance" }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-4 p-4 rounded-xl bg-slate-800/30 border border-slate-800">
                    <div className="p-2 rounded-lg" style={{ backgroundColor: `${accentColor}25` }}>
                      <item.icon className="h-5 w-5" style={{ color: accentColor }} />
                    </div>
                    <div>
                      <h4 className="font-semibold" style={{ color: headingColor }}>{item.title}</h4>
                      <p className="text-sm" style={{ color: textColor }}>{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div 
                className="absolute inset-0 rounded-3xl blur-3xl opacity-20" 
                style={{ background: `linear-gradient(to right, ${accentColor}, ${secondaryColor})` }}
              />
              <div className="relative bg-slate-900 rounded-2xl border border-slate-800 p-8 shadow-2xl">
                {/* Mock Dashboard */}
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${accentColor}25` }}>
                        <GraduationCap className="h-5 w-5" style={{ color: accentColor }} />
                      </div>
                      <div>
                        <p className="font-medium" style={{ color: headingColor }}>Learning Dashboard</p>
                        <p className="text-xs" style={{ color: textColor }}>Real-time progress tracking</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold" style={{ color: accentColor }}>87%</p>
                      <p className="text-xs" style={{ color: textColor }}>Completion</p>
                    </div>
                  </div>
                  
                  <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                    <div 
                      className="h-full w-[87%] rounded-full" 
                      style={{ background: `linear-gradient(to right, ${primaryColor}, ${accentColor})` }}
                    />
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    {[
                      { label: "Courses", value: "12" },
                      { label: "CPD Points", value: "45" },
                      { label: "Badges", value: "8" }
                    ].map((stat, idx) => (
                      <div key={idx} className="text-center p-3 rounded-lg bg-slate-800/50">
                        <p className="text-xl font-bold" style={{ color: headingColor }}>{stat.value}</p>
                        <p className="text-xs" style={{ color: textColor }}>{stat.label}</p>
                      </div>
                    ))}
                  </div>
                  
                  <div 
                    className="p-4 rounded-xl"
                    style={{
                      backgroundColor: `${secondaryColor}1A`,
                      borderColor: `${secondaryColor}33`,
                      borderWidth: '1px'
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <AlertTriangle className="h-5 w-5" style={{ color: secondaryColor }} />
                      <div>
                        <p className="text-sm font-medium" style={{ color: secondaryColor }}>New Regulatory Alert</p>
                        <p className="text-xs" style={{ color: textColor }}>FSCA Notice: Updated AML Requirements</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="relative">
            <div 
              className="absolute inset-0 rounded-3xl blur-3xl opacity-20" 
              style={{ backgroundColor: primaryColor }}
            />
            <div className="relative bg-slate-900 rounded-3xl border border-slate-800 p-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ color: headingColor }}>
                Ready to Transform Your{' '}
                <span style={{ color: primaryColor }}>Learning Experience</span>?
              </h2>
              <p className="text-lg mb-8 max-w-2xl mx-auto" style={{ color: textColor }}>
                {isMaster 
                  ? 'Join thousands of professionals and organisations already using Cerebro Core to drive compliance excellence and workforce development.'
                  : `Empower ${orgName} learners with continuous compliance training, verifiable CPD credentials, and interactive learning labs.`
                }
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/courses"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 text-slate-900 font-semibold rounded-xl transition-all shadow-lg hover:opacity-95"
                  style={{ backgroundColor: primaryColor }}
                >
                  <BookOpen className="h-5 w-5" />
                  Explore Courses
                </Link>
                {isSSOEnabled ? (
                  <Link
                    to="/enterprise-sso"
                    className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-slate-800 text-white font-semibold rounded-xl border border-slate-700 hover:bg-slate-700 transition-all"
                  >
                    <Shield className="h-5 w-5" />
                    Sign In with SSO
                  </Link>
                ) : (
                  <Link
                    to="/login"
                    className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-slate-800 text-white font-semibold rounded-xl border border-slate-700 hover:bg-slate-700 transition-all"
                  >
                    Sign In
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-slate-950 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            {/* Brand Column */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src={logoUrl} 
                  alt={orgName} 
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = CEREBRO_LOGO;
                  }}
                  className="h-8 w-auto object-contain" 
                />
                <span className="text-white font-bold">{orgName}</span>
              </div>
              {isMaster ? (
                <p className="text-sm text-slate-400 leading-relaxed max-w-md">
                  Cerebro Core is the learning intelligence platform of the Navigate Compliance Group (Pty) Ltd - 
                  a South African governance & compliance technology firm. QCTO Accredited.
                </p>
              ) : (
                <p className="text-sm text-slate-400 leading-relaxed max-w-md">
                  {orgName} Learning Platform • Powered by Navigate Cerebro LMS.
                </p>
              )}
              <div className="flex items-center gap-2 mt-4 text-sm text-slate-500">
                <MapPin className="h-4 w-4" />
                {isMaster ? 'Johannesburg, South Africa' : `${orgName} Learning Center`}
              </div>
            </div>
            
            {/* Links Column */}
            <div className="flex flex-col md:items-end">
              <div className="flex items-center gap-4 mb-4">
                <a 
                  href="https://www.linkedin.com/company/navigate-compliance-group/" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 transition-colors"
                >
                  <Linkedin className="h-5 w-5 text-slate-400" />
                  <span className="text-sm text-slate-300">LinkedIn</span>
                </a>
              </div>
              
              {/* Policy Links */}
              <div className="flex flex-wrap gap-4 text-sm">
                <a 
                  href="/responsible-ai"
                  className="flex items-center gap-1.5 text-slate-400 hover:text-white transition-colors"
                >
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                  Responsible AI
                </a>
                <a 
                  href="https://customer-assets-7cd3h4nn.emergentagent.net/job_cpd-compass/artifacts/s6j6bxyk_Privacy%20Policy.pdf" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  Privacy Policy
                </a>
                <a 
                  href="https://customer-assets-7cd3h4nn.emergentagent.net/job_cpd-compass/artifacts/dtzmhzj0_Terms%20of%20Use.pdf" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  Terms of Use
                </a>
                <a 
                  href="https://customer-assets-7cd3h4nn.emergentagent.net/job_cpd-compass/artifacts/c94i8o5l_HITL%20Governance%20Policy.pdf" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  HITL Governance
                </a>
              </div>
            </div>
          </div>
          
          <div className="pt-8 border-t border-slate-800 text-center">
            <p className="text-sm text-slate-500">
              © 2026 Navigate Compliance Group (Pty) Ltd. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
